import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Heart, Eye } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { featuredProducts } from '../../data/products';

const FeaturedProducts = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section className="section-padding bg-dark-900 relative overflow-hidden">
      <div className="container-custom">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="section-title text-gradient">
              Furniture Collection
            </h2>
            <p className="section-subtitle">
              Simple elegant minimal furniture collection
            </p>
          </motion.div>

          {/* Products Grid */}
          <motion.div
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {featuredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                variants={itemVariants}
                className="group"
              >
                <div className="product-card bg-dark-800/30 backdrop-blur-md rounded-2xl overflow-hidden border border-dark-700 hover:border-accent-500/30 transition-all duration-500">
                  {/* Product Image */}
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-300"></div>
                    
                    {/* Floating Actions */}
                    <div className="absolute top-4 right-4 flex flex-col space-y-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <button className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/30 transition-colors duration-300">
                        <Heart className="h-4 w-4 text-white" />
                      </button>
                      <Link
                        to={`/products/${product.id}`}
                        className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/30 transition-colors duration-300"
                      >
                        <Eye className="h-4 w-4 text-white" />
                      </Link>
                    </div>

                    {/* Badge */}
                    {product.originalPrice > product.price && (
                      <div className="absolute top-4 left-4">
                        <span className="bg-accent-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                          Sale
                        </span>
                      </div>
                    )}

                    {/* Price Tag */}
                    <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-black/50 backdrop-blur-md px-3 py-2 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <span className="text-white font-bold">${product.price.toLocaleString()}</span>
                          {product.originalPrice > product.price && (
                            <span className="text-dark-400 text-sm line-through">
                              ${product.originalPrice.toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-accent-400 text-sm font-medium capitalize">
                        {product.category}
                      </span>
                      <div className="flex items-center space-x-1">
                        <Star className="h-4 w-4 text-yellow-400 fill-current" />
                        <span className="text-sm text-dark-300">{product.rating}</span>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-accent-400 transition-colors duration-300">
                      {product.name}
                    </h3>
                    
                    <p className="text-dark-300 text-sm mb-4 leading-relaxed">
                      {product.description}
                    </p>

                    {/* Features */}
                    <div className="mb-6">
                      <div className="flex flex-wrap gap-2">
                        {product.features.slice(0, 2).map((feature, featureIndex) => (
                          <span
                            key={featureIndex}
                            className="text-xs bg-dark-700 text-dark-300 px-2 py-1 rounded-full"
                          >
                            {feature}
                          </span>
                        ))}
                        {product.features.length > 2 && (
                          <span className="text-xs text-accent-400">
                            +{product.features.length - 2} more
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Action Button */}
                    <Link
                      to={`/products/${product.id}`}
                      className="w-full bg-gradient-accent text-white py-3 rounded-lg font-medium flex items-center justify-center space-x-2 hover:bg-accent-600 transition-colors duration-300 group"
                    >
                      <span>View Details</span>
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* View All Button */}
          <motion.div variants={itemVariants} className="text-center mt-12">
            <Link
              to="/products"
              className="inline-flex items-center space-x-2 btn-secondary text-lg px-8 py-4"
            >
              <span>View All Products</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Background Elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-accent-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-accent-600/5 rounded-full blur-3xl"></div>
    </section>
  );
};

export default FeaturedProducts;